"use client";

import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import styles from "./PaginationControls.module.css";

type PageItem =
  | { type: "page"; n: number }
  | { type: "ellipsis"; id: string };

type Props = {
  totalPages: number;
  page: number;
};

function getPageItems(current: number, total: number): PageItem[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, index) => ({
      type: "page",
      n: index + 1,
    }));
  }

  const items: PageItem[] = [{ type: "page", n: 1 }];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  if (start > 2) {
    items.push({ type: "ellipsis", id: "start" });
  }

  for (let n = start; n <= end; n += 1) {
    items.push({ type: "page", n });
  }

  if (end < total - 1) {
    items.push({ type: "ellipsis", id: "end" });
  }

  items.push({ type: "page", n: total });
  return items;
}

function PageItemView({
  item,
  page,
  hrefFor,
}: {
  item: PageItem;
  page: number;
  hrefFor: (n: number) => string;
}) {
  switch (item.type) {
    case "page": {
      if (item.n === page) {
        return (
          <span aria-current="page" className={styles.current}>
            {item.n}
          </span>
        );
      }

      return (
        <Link href={hrefFor(item.n)} className={styles.page}>
          {item.n}
        </Link>
      );
    }
    case "ellipsis":
      return (
        <span className={styles.ellipsis} aria-hidden="true">
          …
        </span>
      );
    default: {
      const _never: never = item;
      return _never;
    }
  }
}

export default function PaginationControls({ totalPages, page }: Props) {
  const searchParams = useSearchParams();
  const pathname = usePathname();

  if (totalPages <= 1) {
    return null;
  }

  const hrefFor = (nextPage: number) => {
    const params = new URLSearchParams(searchParams);
    if (nextPage <= 1) {
      params.delete("page");
    } else {
      params.set("page", String(nextPage));
    }
    params.delete("perPage");
    const queryString = params.toString();
    return queryString ? `${pathname}?${queryString}` : pathname;
  };

  const items = getPageItems(page, totalPages);
  const hasPrev = page > 1;
  const hasNext = page < totalPages;

  return (
    <nav className={styles.nav} aria-label="Pagination">
      {hasPrev ? (
        <Link
          href={hrefFor(page - 1)}
          className={styles.step}
          rel="prev"
          aria-label="Previous page"
        >
          Previous
        </Link>
      ) : (
        <span className={styles.stepDisabled} aria-disabled="true">
          Previous
        </span>
      )}
      {items.map((item) => (
        <PageItemView
          key={item.type === "page" ? `page-${item.n}` : item.id}
          item={item}
          page={page}
          hrefFor={hrefFor}
        />
      ))}
      {hasNext ? (
        <Link
          href={hrefFor(page + 1)}
          className={styles.step}
          rel="next"
          aria-label="Next page"
        >
          Next
        </Link>
      ) : (
        <span className={styles.stepDisabled} aria-disabled="true">
          Next
        </span>
      )}
    </nav>
  );
}
